import React, { useState } from "react";
import { Row, Col } from "reactstrap";
import NeoButton from "../../../components/Common/NeoButton";
import NeoInput from "../../../components/Common/NeoInput";
import NeoModal from "../../../components/Common/NeoModal";
import NeoCard from "../../../components/Common/NeoCard";
import NeoBadge from "../../../components/Common/NeoBadge";
import NeoTable from "../../../components/Common/NeoTable";
import Breadcrumb from "../../../components/Common/Breadcrumb";
import usePlatformPlans, { mapEffectiveToCommercialFeatures } from "./hooks/usePlatformPlans";
import { FEATURE_LABELS, FEATURE_KEYS, DEFAULT_PLAN_FORM } from "./constants";
import { PLAN_FEATURES } from "../../../constants/plans";

const PlatformPlans = () => {
    const { plans, loading, saveCommercialPlan, deleteCommercialPlan } = usePlatformPlans();

    const [modalOpen, setModalOpen] = useState(false);
    const [editingPlan, setEditingPlan] = useState(null);
    const [form, setForm] = useState(DEFAULT_PLAN_FORM);

    const openCreateModal = () => {
        setEditingPlan(null);
        setForm(DEFAULT_PLAN_FORM);
        setModalOpen(true);
    };

    const openEditModal = (plan) => {
        setEditingPlan(plan);
        setForm({ ...plan });
        setModalOpen(true);
    };

    const handleFeatureChange = (featureKey) => {
        setForm(prev => ({
            ...prev,
            features: { ...prev.features, [featureKey]: !prev.features[featureKey] },
        }));
    };

    const handleBasePlanChange = (basePlanId) => {
        const defaultTechFeatures = PLAN_FEATURES[basePlanId] || {};
        const commercialFeatures = mapEffectiveToCommercialFeatures(defaultTechFeatures, basePlanId);
        
        setForm(prev => ({
            ...prev,
            basePlanId,
            features: commercialFeatures,
        }));
    };

    const handleSavePlan = async (e) => {
        if (e) e.preventDefault();
        await saveCommercialPlan(editingPlan ? { ...form, id: editingPlan.id } : form);
        setModalOpen(false);
    };

    const handleDeletePlan = async (plan) => {
        const confirmed = window.confirm(`Tem certeza que deseja excluir o plano "${plan.name}"? Esta ação não poderá ser desfeita.`);
        if (!confirmed) return;
        try {
            await deleteCommercialPlan(plan.id);
        } catch (err) {
            // O erro já é tratado no hook
        }
    };

    const columns = [
        {
            key: "name",
            label: "Nome do Plano",
            width: "20%",
            render: (row) => <span className="fw-bold text-dark fs-6">{row.name}</span>,
        },
        {
            key: "monthlyPrice",
            label: "Preço Mensal",
            width: "15%",
            render: (row) => <span className="fw-semibold text-secondary">R$ {(row.monthlyPrice || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</span>,
        },
        {
            key: "studentLimit",
            label: "Limite Alunos",
            width: "15%",
            render: (row) => <span>{row.studentLimit === -1 ? "Ilimitado" : `${row.studentLimit} alunos`}</span>,
        },
        {
            key: "features",
            label: "Recursos Principais",
            width: "30%",
            render: (row) => {
                const criticalKeys = [
                    "financial_module",
                    "crm_module",
                    "class_management",
                    "workout_creation",
                    "kiosk",
                    "ai_workout_generation",
                    "gamification",
                    "planning_module"
                ];
                const activeCritical = criticalKeys.filter(k => row.features?.[k]);
                const otherCount = Object.keys(row.features || {}).filter(k => row.features[k] && !criticalKeys.includes(k)).length;
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {activeCritical.map(k => (
                            <NeoBadge key={k} color={["financial_module", "crm_module"].includes(k) ? "success" : "muted"}>
                                {FEATURE_LABELS[k] || k}
                            </NeoBadge>
                        ))}
                        {otherCount > 0 && (
                            <NeoBadge color="info">
                                +{otherCount} outros
                            </NeoBadge>
                        )}
                    </div>
                );
            }
        },
        {
            key: "active",
            label: "Status",
            width: "10%",
            render: (row) => (
                <NeoBadge color={row.active ? "success" : "danger"}>
                    {row.active ? "Ativo" : "Inativo"}
                </NeoBadge>
            ),
        },
        {
            key: "actions",
            label: "Ações",
            width: "10%",
            align: "center",
            render: (row) => (
                <div className="d-flex align-items-center justify-content-center gap-1">
                    <NeoButton variant="soft-secondary" size="sm" square icon="mdi-pencil-outline" onClick={() => openEditModal(row)} title="Editar Plano" />
                    {row.source === "firestore" && (
                        <NeoButton 
                            variant="soft-danger" 
                            size="sm" 
                            square 
                            icon="mdi-trash-can-outline" 
                            onClick={() => handleDeletePlan(row)} 
                            title="Excluir Plano" 
                        />
                    )}
                </div>
            ),
        },
    ];

    const modalFooter = (
        <>
            <NeoButton variant="secondary" onClick={() => setModalOpen(false)}>Cancelar</NeoButton>
            <NeoButton variant="primary" onClick={handleSavePlan}>Salvar Plano</NeoButton>
        </>
    );

    return (
        <div className="pb-5">
            <div className="mb-4">
                <Breadcrumb title="Planos" />
            </div>

            <NeoCard
                title="Lista de Planos"
                subtitle="Gerencie a prateleira de planos ofertados no NeoApp, definindo preços, vigência e limites técnicos."
                headerIcon="mdi-tag-multiple-outline"
                headerActions={
                    <NeoButton variant="primary" size="sm" onClick={openCreateModal}>
                        <i className="ph-light ph-plus fs-5 me-1" /> Criar Novo Plano
                    </NeoButton>
                }
                thinBorder={true}
                bodyClassName="p-0"
            >
                <NeoTable columns={columns} data={plans} loading={loading} emptyMessage="Nenhum plano comercial encontrado" />
            </NeoCard>

            <NeoModal
                isOpen={modalOpen}
                toggle={() => setModalOpen(false)}
                title={editingPlan ? `Editar Plano: ${editingPlan.name}` : "Criar Novo Plano Comercial"}
                size="lg"
                footerActions={modalFooter}
            >
                <Row className="g-3 mb-4">
                    <Col md={6}>
                        <NeoInput label="Nome do Plano comercial *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                    </Col>
                    <Col md={6}>
                        <NeoInput 
                            type="select" 
                            label="Plano Base (Matriz Técnica) *" 
                            value={form.basePlanId || "basic"} 
                            onChange={(e) => handleBasePlanChange(e.target.value)}
                        >
                            <option value="personal">Personal</option>
                            <option value="basic">Básico</option>
                            <option value="master">Master</option>
                            <option value="neo">NEO</option>
                        </NeoInput>
                    </Col>
                    <Col md={6}>
                        <NeoInput label="Preço Mensal (R$) *" type="number" value={form.monthlyPrice} onChange={(e) => setForm({ ...form, monthlyPrice: Number(e.target.value) })} />
                    </Col>
                    <Col md={6}>
                        <NeoInput label="Limite de Alunos *" placeholder="-1 para Ilimitado" type="number" value={form.studentLimit} onChange={(e) => setForm({ ...form, studentLimit: Number(e.target.value) })} />
                    </Col>
                    <Col md={6}>
                        <NeoInput label="Preço Metodologia NEO (R$) *" type="number" value={form.methodologyNeoPrice} onChange={(e) => setForm({ ...form, methodologyNeoPrice: Number(e.target.value) })} />
                    </Col>
                </Row>

                <h6 className="fw-bold mb-3" style={{ color: "var(--color-brand-primary)" }}>Módulos & Recursos Liberados</h6>
                <Row className="g-3">
                    {FEATURE_KEYS.map(k => (
                        <Col key={k} md={4} xs={6}>
                            <div className="form-check form-switch py-2 ps-5 pe-3 rounded hover-bg-light transition-all mb-0" style={{ cursor: "pointer", minHeight: "38px" }}>
                                <input 
                                    type="checkbox" 
                                    role="switch" 
                                    id={`feature-${k}`}
                                    checked={!!form.features?.[k]} 
                                    onChange={() => handleFeatureChange(k)} 
                                    className="form-check-input mt-2 cursor-pointer" 
                                />
                                <label 
                                    className="form-check-label small mb-0 ms-1 cursor-pointer text-truncate d-block pt-1" 
                                    htmlFor={`feature-${k}`}
                                    title={FEATURE_LABELS[k]}
                                >
                                    {FEATURE_LABELS[k]}
                                </label>
                            </div>
                        </Col>
                    ))}
                </Row>
            </NeoModal>
        </div>
    );
};

export default PlatformPlans;
